import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  UsersIcon, 
  ClipboardIcon, 
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { environment } from './environment';
import { getToken } from './App';

const Reports = () => {
  const [reportData, setReportData] = useState({
    sessions: [],
    clients: [],
    users: [],
    isLoading: true,
    dateRange: 'all', // Changed from '30' to 'all'
    selectedType: 'all'
  });

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    clientId: 'all',
    sessionType: 'all',
    staffMember: 'all',
    dateRange: 'all'  // Changed from empty string to 'all'
  });

  useEffect(() => {
    fetchReportData();
  }, [filters]);

  const fetchReportData = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      console.log('Fetching report data...');
      console.log('API URL:', environment.apiUrl);

      // Fetch all data
      const [sessionsRes, clientsRes, usersRes] = await Promise.all([
        fetch(`${environment.apiUrl}/sessions`, { headers }),
        fetch(`${environment.apiUrl}/api/Clients`, { headers }),
        fetch(`${environment.apiUrl}/users`, { headers })
      ]);

      console.log('Sessions response status:', sessionsRes.status);
      console.log('Clients response status:', clientsRes.status);
      console.log('Users response status:', usersRes.status);

      const sessions = sessionsRes.ok ? await sessionsRes.json() : [];
      const clients = clientsRes.ok ? await clientsRes.json() : [];
      const users = usersRes.ok ? await usersRes.json() : [];

      console.log('Sessions loaded:', sessions.length);
      console.log('Clients loaded:', clients.length);
      console.log('Users loaded:', users.length);
      console.log('Sample session:', sessions[0]);

      setReportData({ sessions, clients, users, isLoading: false });
    } catch (error) {
      console.error('Error fetching report data:', error);
      setReportData(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const { sessions, clients, users } = reportData;
    
    console.log('Calculating stats with:', {
      totalSessions: sessions.length,
      totalClients: clients.length,
      filters: filters
    });
    
    let filteredSessions = [...sessions];

    // Filter by date range (if no specific dates are set)
    if (!filters.startDate && !filters.endDate && filters.dateRange !== 'all') {
      const now = new Date();
      const daysAgo = new Date(now.getTime() - (parseInt(filters.dateRange) * 24 * 60 * 60 * 1000));
      console.log('Filtering by date range:', { now, daysAgo, dateRange: filters.dateRange });
      filteredSessions = filteredSessions.filter(session => 
        new Date(session.created_at) >= daysAgo
      );
      console.log('Sessions after date filter:', filteredSessions.length);
    }

    // Filter by specific start date
    if (filters.startDate) {
      filteredSessions = filteredSessions.filter(session => 
        new Date(session.created_at) >= new Date(filters.startDate)
      );
    }

    // Filter by specific end date
    if (filters.endDate) {
      filteredSessions = filteredSessions.filter(session => 
        new Date(session.created_at) <= new Date(filters.endDate + 'T23:59:59')
      );
    }

    // Filter by session type
    if (filters.sessionType && filters.sessionType !== 'all') {
      filteredSessions = filteredSessions.filter(session => 
        session.emr_name === filters.sessionType
      );
    }

    // Filter by client ID
    if (filters.clientId && filters.clientId !== 'all') {
      filteredSessions = filteredSessions.filter(session => 
        session.client_id === filters.clientId
      );
    }

    // Filter by staff member (for YSL sessions)
    if (filters.staffMember && filters.staffMember !== 'all') {
      filteredSessions = filteredSessions.filter(session => 
        session.staff_providing_service === filters.staffMember
      );
    }

    // Session type breakdown
    const sessionTypes = filteredSessions.reduce((acc, session) => {
      const type = session.emr_name || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    // No-show rate
    const noShowSessions = filteredSessions.filter(s => s.is_no_show);
    const noShowRate = filteredSessions.length > 0 ? 
      (noShowSessions.length / filteredSessions.length * 100).toFixed(1) : 0;

    // Off-site delivery rate
    const offSiteSessions = filteredSessions.filter(s => s.delivered_off_site);
    const offSiteRate = filteredSessions.length > 0 ? 
      (offSiteSessions.length / filteredSessions.length * 100).toFixed(1) : 0;

    // Average sessions per client (only count clients that have sessions)
    const clientsWithSessions = new Set(filteredSessions.map(s => s.client_id));
    const avgSessionsPerClient = clientsWithSessions.size > 0 ? 
      (filteredSessions.length / clientsWithSessions.size).toFixed(1) : 0;

    return {
      totalSessions: filteredSessions.length,
      totalClients: clientsWithSessions.size,
      totalUsers: users.length,
      sessionTypes,
      noShowRate,
      offSiteRate,
      avgSessionsPerClient,
      recentSessions: filteredSessions
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        // Remove the .slice(0, 10) to show ALL sessions
    };
  };

  const stats = calculateStats();

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center mt-2">
              {trend > 0 ? (
                <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Math.abs(trend)}% from last period
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
      {children}
    </div>
  );

  const SessionTypeChart = () => {
    const { sessionTypes } = stats;
    const total = Object.values(sessionTypes).reduce((sum, count) => sum + count, 0);
    
    return (
      <div className="space-y-4">
        {Object.entries(sessionTypes).map(([type, count]) => {
          const percentage = total > 0 ? (count / total * 100).toFixed(1) : 0;
          return (
            <div key={type} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span className="font-medium text-slate-700">{type}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-32 bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-slate-600 w-12 text-right">
                  {count} ({percentage}%)
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const RecentSessionsTable = () => (
    <div className="overflow-hidden">
      {/* Always show scrollable table when there are more than 10 sessions */}
      {stats.recentSessions.length > 10 ? (
        <div className="overflow-y-auto border border-slate-200 rounded-lg bg-white" style={{height: '320px'}}>
          {/* Session count indicator */}
          <div className="sticky top-0 z-20 bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-700 font-medium">
            📊 Showing all {stats.recentSessions.length} sessions - Scroll to see more
          </div>
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50 sticky top-12 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {stats.recentSessions.map((session, index) => (
                <tr key={session.id || index} className="hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                    {session.client_id_name || 'Unknown'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs font-medium">
                      {session.emr_name || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                    {new Date(session.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {session.is_no_show ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircleIcon className="w-3 h-3 mr-1" />
                        No Show
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Completed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <table className="min-w-full divide-y divide-slate-200 bg-white rounded-lg border border-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {stats.recentSessions.map((session, index) => (
              <tr key={session.id || index} className="hover:bg-slate-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                  {session.client_id_name || 'Unknown'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs font-medium">
                    {session.emr_name || 'Unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                  {new Date(session.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {session.is_no_show ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <XCircleIcon className="w-3 h-3 mr-1" />
                      No Show
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="w-3 h-3 mr-1" />
                      Completed
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const FilterSection = () => {
    // Get unique staff members from sessions
    const staffMembers = [...new Set(reportData.sessions
      .map(s => s.staff_providing_service)
      .filter(Boolean)
    )];

    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Filters</h3>
          <FunnelIcon className="w-5 h-5 text-slate-500" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Date Range</label>
            <select 
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Session Type</label>
            <select 
              value={filters.sessionType}
              onChange={(e) => setFilters(prev => ({ ...prev, sessionType: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {[...new Set(reportData.sessions.map(s => s.emr_name).filter(Boolean))].map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Client</label>
            <select 
              value={filters.clientId}
              onChange={(e) => setFilters(prev => ({ ...prev, clientId: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Clients</option>
              {reportData.clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.first_name} {client.last_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Staff Member</label>
            <select 
              value={filters.staffMember}
              onChange={(e) => setFilters(prev => ({ ...prev, staffMember: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Staff</option>
              {staffMembers.map(staff => (
                <option key={staff} value={staff}>{staff}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
            <input 
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
            <input 
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        {/* Clear Filters Button */}
        <div className="mt-4 flex justify-between items-center">
          {/* Active Filters Summary */}
          <div className="flex flex-wrap gap-2">
            {/* Filter Count */}
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
              {stats.totalSessions} sessions found
            </span>
            
            {/* Only show filter badges when actual filtering is happening */}
            {filters.sessionType !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Type: {filters.sessionType}
              </span>
            )}
            {filters.clientId && filters.clientId !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Client: {reportData.clients.find(c => c.id === filters.clientId)?.first_name} {reportData.clients.find(c => c.id === filters.clientId)?.last_name}
              </span>
            )}
            {filters.staffMember !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Staff: {filters.staffMember}
              </span>
            )}
            {filters.startDate && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                From: {new Date(filters.startDate).toLocaleDateString()}
              </span>
            )}
            {filters.endDate && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                To: {new Date(filters.endDate).toLocaleDateString()}
              </span>
            )}
            {filters.dateRange && filters.dateRange !== 'all' && !filters.startDate && !filters.endDate && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Last {filters.dateRange} days
              </span>
            )}
          </div>
          
          <button
            onClick={() => setFilters({
              startDate: '',
              endDate: '',
              clientId: 'all',
              sessionType: 'all',
              staffMember: 'all',
              dateRange: 'all'
            })}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    );
  };

  if (reportData.isLoading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Analytics & Reports</h1>
            <p className="text-purple-100 text-lg">Comprehensive insights into your session management</p>
          </div>
          <div className="flex space-x-3">
            <button className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2">
              <EyeIcon className="w-4 h-4" />
              <span>Preview</span>
            </button>
            <button className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2">
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <FilterSection />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Sessions"
          value={stats.totalSessions}
          subtitle={filters.dateRange === 'all' ? 'All time' : `Last ${filters.dateRange} days`}
          icon={ClipboardIcon}
          color="bg-blue-500"
          trend={12}
        />
        <StatCard
          title="Active Clients"
          value={stats.totalClients}
          subtitle="Total registered"
          icon={UsersIcon}
          color="bg-green-500"
          trend={8}
        />
        <StatCard
          title="No-Show Rate"
          value={`${stats.noShowRate}%`}
          subtitle="Of total sessions"
          icon={XCircleIcon}
          color="bg-red-500"
          trend={-5}
        />
        <StatCard
          title="Off-Site Rate"
          value={`${stats.offSiteRate}%`}
          subtitle="Of total sessions"
          icon={MapPinIcon}
          color="bg-purple-500"
          trend={15}
        />
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Session Types Distribution">
          <SessionTypeChart />
        </ChartCard>
        
        <ChartCard title="Performance Metrics">
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <ClockIcon className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-slate-700">Avg Sessions per Client</span>
              </div>
              <span className="text-2xl font-bold text-blue-600">{stats.avgSessionsPerClient}</span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <CalendarDaysIcon className="w-5 h-5 text-green-500" />
                <span className="font-medium text-slate-700">Total Users</span>
              </div>
              <span className="text-2xl font-bold text-green-600">{stats.totalUsers}</span>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Recent Sessions Table */}
      <ChartCard title={`Sessions (${stats.recentSessions.length} total)`} className="mb-6">
        <RecentSessionsTable />
      </ChartCard>
    </div>
  );
};

export default Reports; 